import { Injectable } from '@angular/core';
import { AppState } from '@api-ui-app/src/app/app.state';
import { ECommerceProductWizardDetailsService } from '@api-ui-app/src/app/e-commerce-stores/services/e-commerce-product-wizard/e-commerce-product-wizard-details.service';
import { SUPPORTED_RETAIL_CURRENCIES } from '@api-ui-app/src/app/lib/constants';
import { getSelectedClientCountry, getSelectedClientId } from '@api-ui-app/src/app/ngrx/auth.reducer';
import * as eCommerceProductWizardActions from '@api-ui-app/src/app/ngrx/e-commerce-product-wizard.actions';
import {
  DesignAssetCostsCollection,
  PriceCollection,
} from '@api-ui-app/src/app/ngrx/e-commerce-product-wizard.reducer';
import {
  getProductDetails,
  getSelectedPricesCountry,
} from '@api-ui-app/src/app/ngrx/e-commerce-product-wizard.selector';
import { getStoreCurrency } from '@api-ui-app/src/app/ngrx/e-commerce-stores.selector';
import { AssetsCollection } from '@gelato-api-ui/core/designs/assets-collection';
import { DesignStructure } from '@gelato-api-ui/core/designs/design-structure';
import { hasAppliedDesignTemplate } from '@gelato-api-ui/core/designs/helpers/hasAppliedDesignTemplate';
import { DesignCostService } from '@gelato-api-ui/core/designs/services/design-cost.service';
import { DesignGraphQlService } from '@gelato-api-ui/core/designs/services/design-graph-ql.service';
import { CustomTrimValuesMm } from '@gelato-api-ui/core/e-commerce/custom-trim-values-mm';
import { EProductVariant } from '@gelato-api-ui/core/e-commerce/e-product-variant';
import { getCustomTrimByDimensions } from '@gelato-api-ui/core/e-commerce/helpers/getCustomTrimByDimensions';
import { getCustomTrimValuesMm } from '@gelato-api-ui/core/e-commerce/helpers/getCustomTrimValuesMm';
import { getProductVariantKey } from '@gelato-api-ui/core/e-commerce/helpers/getProductVariantKey';
import { EProductVariantsCollection } from '@gelato-api-ui/core/e-commerce/types/e-product-variants-collection';
import { ProductTypeUid } from '@gelato-api-ui/core/product-catalogue/product-type-uid.enum';
import { ProductTypeUidService } from '@gelato-api-ui/core/product-catalogue/services/product-type-uid.service';
import { toPriceNumber } from '@gelato-api-ui/core/product-prices/helpers/toPriceNumber';
import { SanityProduct } from '@gelato-api-ui/core/sanity/sanity-product';
import { SanityProductVariantWallpaperType } from '@gelato-api-ui/core/sanity/sanity-product-variant';
import { PriceListGraphQlService } from '@gelato-api-ui/sdk/src/lib/shared/price-sdk/price-list-graph-ql.service';
import { Store } from '@ngrx/store';
import { getSelectedProductFromSanityContent } from '@product-catalogue/src/lib/ngrx/product-catalogue.reducer';
import { PriceList, PriceListItem } from '@product-catalogue/src/lib/product-catalogue/lib/price-list';
import { Observable, forkJoin, of } from 'rxjs';
import { catchError, delay, map, switchMap, take, tap, withLatestFrom } from 'rxjs/operators';
import { ShowGeneralErrorNotification } from '@gelato-api-ui/ui-kit/src/lib/notification/notification.actions';
import { ProductUidWithDimensions } from '@product-catalogue/src/lib/lib/price/ProductUidsWithDimensions';

@Injectable({
  providedIn: 'root',
})
export class ECommerceProductWizardPricesService {
  storeCurrency$ = this.store
    .select(getStoreCurrency)
    .pipe(map(storeCurrency => (SUPPORTED_RETAIL_CURRENCIES.includes(storeCurrency) ? storeCurrency : 'EUR')));
  suggestedCountry$ = this.store.select(getSelectedClientCountry);
  selectedClientId$ = this.store.select(getSelectedClientId);
  selectedCountryIsoCode$ = this.store.select(getSelectedPricesCountry);
  productDetails$ = this.store.select(getProductDetails);
  sanityProduct$ = this.store.select(getSelectedProductFromSanityContent);

  constructor(
    private readonly productTypeUidService: ProductTypeUidService,
    private readonly designGraphQlService: DesignGraphQlService,
    private readonly designCostService: DesignCostService,
    private readonly eCommerceProductWizardDetailsService: ECommerceProductWizardDetailsService,
    private readonly priceListGraphQlService: PriceListGraphQlService,
    private readonly store: Store<AppState>,
  ) {}

  fetchAssetCosts(
    clientId: string,
    assetsCollection: AssetsCollection,
    sortedProductVariants: EProductVariant[],
    currency: string,
    productCosts: PriceCollection,
  ): Observable<DesignAssetCostsCollection> {
    if (!currency) {
      return of({});
    }

    const firstProductUid = sortedProductVariants[0]?.productUid;

    return this.productTypeUidService.getByProductUid(firstProductUid).pipe(
      catchError(() => of(null)),
      switchMap((productTypeUid: ProductTypeUid): Observable<DesignAssetCostsCollection> => {
        if (!productTypeUid) {
          return of({});
        }

        const observables = sortedProductVariants.map((loopProductVariant: EProductVariant): Observable<number[]> => {
          if (!loopProductVariant.designStructureJson) {
            return of(null);
          }

          const { productUid, designStructureJson } = loopProductVariant;
          const productCost: number = productCosts[productUid];

          if (!productCost) {
            return of(null);
          }

          const designStructure: DesignStructure = designStructureJson ? JSON.parse(designStructureJson) : null;

          return this.designCostService
            .getDesignAssetPrices(clientId, designStructure, assetsCollection, productTypeUid, currency, productCost)
            .pipe(catchError(() => of(null)));
        });

        return forkJoin(observables).pipe(
          map((responses: number[][]): DesignAssetCostsCollection => {
            const designAssetCosts: DesignAssetCostsCollection = {};

            responses.forEach((loopAssetCostsArray: number[], index: number) => {
              const { productUid } = sortedProductVariants[index];

              designAssetCosts[productUid] = loopAssetCostsArray;
            });

            return designAssetCosts;
          }),
        );
      }),
    );
  }

  fetchDesignTemplateCosts(sortedProductVariants: EProductVariant[], currency: string): Observable<PriceCollection> {
    const designStructures = sortedProductVariants.map((loopProductVariant: EProductVariant): DesignStructure => {
      if (!loopProductVariant.designStructureJson) {
        return null;
      }

      return JSON.parse(loopProductVariant.designStructureJson);
    });

    const someVariantsHasAppliedDesignTemplates = designStructures.some(
      (loopDesignStructure: DesignStructure): boolean => hasAppliedDesignTemplate(loopDesignStructure),
    );

    if (!someVariantsHasAppliedDesignTemplates) {
      return of({});
    }

    return this.designGraphQlService.getDesignTemplatePrice(currency).pipe(
      map((designTemplatePrice: number): PriceCollection => {
        const designTemplateCosts: PriceCollection = {};

        sortedProductVariants.forEach((loopProductVariant: EProductVariant, index: number) => {
          if (hasAppliedDesignTemplate(designStructures[index])) {
            designTemplateCosts[loopProductVariant.productUid] = designTemplatePrice;
          }
        });

        return designTemplateCosts;
      }),
    );
  }

  fetchProductCostsByCustomParameters(
    variants: EProductVariantsCollection,
    pageCount: number,
    sortedProductVariants$: Observable<EProductVariant[]>,
  ): Observable<number[]> {
    return this.eCommerceProductWizardDetailsService.fetchProductDetails(sortedProductVariants$).pipe(
      delay(300),
      withLatestFrom(
        this.storeCurrency$,
        this.suggestedCountry$,
        this.selectedClientId$,
        this.selectedCountryIsoCode$,
        this.productDetails$,
        this.sanityProduct$,
      ),
      switchMap(
        ([
          ,
          storeCurrency,
          suggestedCountry,
          selectedClientId,
          selectedCountryIsoCode,
          productDetails,
          sanityProduct,
        ]) => {
          const productsForPriceList = this.getProductsForPriceList(variants, sanityProduct);

          return this.fetchPriceListArray(
            productsForPriceList,
            selectedCountryIsoCode || suggestedCountry,
            storeCurrency,
            pageCount,
            selectedClientId,
            productDetails?.hasFlatShippingPriceType,
          ).pipe(
            take(1),
            map(priceList => this.mapPriceListToVariants(priceList, variants, sanityProduct)),
            tap(priceList => {
              this.processProductCosts(priceList, pageCount);
            }),
            catchError(() => {
              new ShowGeneralErrorNotification();
              return of(null);
            }),
          );
        },
      ),
    );
  }

  private fetchPriceListArray(
    products: ProductUidWithDimensions[],
    country: string,
    currency: string,
    pageCount: number,
    clientId: string,
    useFlatPrices: boolean,
  ): Observable<PriceListItem[]> {
    if (!products?.length) {
      return of([]);
    }

    return this.priceListGraphQlService.fetchPriceListArray(
      products,
      country,
      currency,
      pageCount,
      clientId,
      useFlatPrices,
      true,
    );
  }

  private mapPriceListToVariants(
    priceList: PriceListItem[],
    variants: EProductVariantsCollection,
    sanityProduct: SanityProduct,
  ): PriceList {
    return Object.values(variants).reduce((res, variant) => {
      const width = variant?.customTrim?.width?.value || null;
      const height = variant?.customTrim?.height?.value || null;
      const isSample = this.isVariantWallpaperSample(variant, sanityProduct);
      const price = priceList.find(
        p => p.productUid === variant.productUid && (isSample || (p.width === width && p.height === height)),
      );
      const key = width && height ? `${price.productUid}+width=${width}+height=${height}` : price.productUid;
      return { ...res, [key]: { ...price, width, height } };
    }, {});
  }

  private getProductsForPriceList(variants: EProductVariantsCollection, sanityProduct: SanityProduct) {
    return Object.values(variants).map(variant => {
      return {
        productUid: variant.productUid,
        ...this.getCustomTrimValuesMm(variant, sanityProduct),
      };
    });
  }

  private getCustomTrimValuesMm(variant, sanityProduct: SanityProduct): CustomTrimValuesMm | {} {
    return this.isVariantWallpaperSample(variant, sanityProduct) ? {} : getCustomTrimValuesMm(variant.customTrim);
  }

  private isVariantWallpaperSample(variant: EProductVariant, sanityProduct: SanityProduct) {
    const variation = sanityProduct?.productVariations?.find(v => v.productUid === variant.productUid);
    return variation?.wallpaperType === SanityProductVariantWallpaperType.sample;
  }

  private processProductCosts(priceList: PriceList, pageCount) {
    if (priceList) {
      const productCostsCollection: PriceCollection = {};
      const productShippingCostsCollection: PriceCollection = {};
      Object.keys(priceList).forEach((key: string) => {
        const priceListItem = priceList[key];
        const productVariantKey = getProductVariantKey({
          productUid: priceListItem.productUid,
          pageCount,
          customTrim: getCustomTrimByDimensions(priceListItem.width, priceListItem.height),
        });

        productCostsCollection[productVariantKey] = toPriceNumber(priceListItem.product);
        productShippingCostsCollection[productVariantKey] = toPriceNumber(priceListItem.shipment);

        this.store.dispatch(new eCommerceProductWizardActions.DecreaseActivePriceFetchingRequestsCount());
      });

      this.store.dispatch(
        eCommerceProductWizardActions.setProductAndShippingCostsBatch({
          productCosts: productCostsCollection,
          productShippingCosts: productShippingCostsCollection,
        }),
      );
    }
  }
}
